import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import {
  ExtraVideoDetails,
  ListingDetails,
  PersonalizedSearchForm,
  VideoDetails,
} from '@keaze/web/common/interfaces';
import { OnVideoClick, OnChange } from './videos.types';
import { LocalStorageKeys, QueryModals } from '@keaze/web/common/enums';
import { GTMViewListing } from '@keaze/web/libs/GTM';
import { useLocalStorage } from '@keaze/web/hooks';
import { DEFAULT_PERSONALIZED_SEARCH_DATA } from '@keaze/web/utils/defaultValues';

const DEFAULT_EXTRA_DETAILS: ExtraVideoDetails = {
  embedCode: '',
  thumbnailUrl: '',
  videoId: '',
  type: '',
  title: '',
};

type UseVideosExpected = {
  isVideoModal: boolean;
  embedCode: string;
  onVideoClick: OnVideoClick;
};

type UseVideosArgs = {
  listing?: ListingDetails;
  videos: VideoDetails[];
  onChange: OnChange;
};

type UseVideos = (args: UseVideosArgs) => UseVideosExpected;

type GetCurrentVideo = (videos: VideoDetails[], id: string) => VideoDetails;

export const extraDetailsDataWithDefault = (
  extraDetails: ExtraVideoDetails | null
) => {
  if (!extraDetails) {
    return DEFAULT_EXTRA_DETAILS;
  }

  return extraDetails;
};

const getCurrentVideo: GetCurrentVideo = (videos, id) =>
  videos.find(({ extraDetails }) => {
    const { videoId } = extraDetailsDataWithDefault(extraDetails);

    return videoId == id;
  }) as VideoDetails;

export const useVideos: UseVideos = ({ listing, videos, onChange }) => {
  const router = useRouter();
  const isVideoModal = !!(router.query[QueryModals.video] as string);
  const [currentVideo, setCurrentVideo] = useState<VideoDetails | null>(null);
  const [
    { householdIncome, estimatedDeposit },
  ] = useLocalStorage<PersonalizedSearchForm>(
    LocalStorageKeys.personalizedSearch,
    DEFAULT_PERSONALIZED_SEARCH_DATA
  );

  const embedCode =
    currentVideo === null || currentVideo.extraDetails === null
      ? ''
      : currentVideo.extraDetails.embedCode;

  useEffect(() => {
    const videoId = router.query[QueryModals.video] as string;

    if (videoId) {
      const currentVideo = getCurrentVideo(videos, videoId);

      setCurrentVideo(currentVideo);
    }
  }, [videos, router]);

  useEffect(() => {
    if (isVideoModal && !!listing) {
      GTMViewListing({
        event: 'view_listing_media',
        listingSchemes: listing.schemes,
        listingSlug: listing.slug,
        householdIncome,
        estimatedDeposit,
      });
    }
  }, [isVideoModal, listing, householdIncome, estimatedDeposit]);

  const handleVideoClick: OnVideoClick = (id) => {
    if (!id) {
      return onChange();
    }

    setCurrentVideo(getCurrentVideo(videos, id));
    return onChange(id);
  };

  return {
    isVideoModal,
    embedCode,
    onVideoClick: handleVideoClick,
  };
};
